import { environment } from '../../../../environments/environment';
import {
    catchError,
    concatMap,
    defer,
    EMPTY,
    filter,
    from,
    map,
    mergeMap,
    Observable,
    of,
    throwError,
    toArray
} from 'rxjs';
import { ILegacyPaginatedComments } from '../interfaces/ILegacyPaginatedComments';
import { inject, Injectable } from '@angular/core';
import { IRequestComment } from '../interfaces/IRequestComment';
import { AbstractCommentService } from './abstract-comment.service';
import { PartialComment } from '../types/PartialComment';
import { AbstractAttachmentService } from './abstract-attachment.service';
import { NamAttachmentService } from '../../../nam/services/attachment.service';
import { EFileEntity } from '@shs-digi-care-monorepo/interfaces';
import { Paginated, PageMetadataWithCursor } from '../models/paginated.model';
import { RequestComment } from '../models/comment.model';
import { UploadFile } from '../interfaces/uploadFile';
import { IFile } from '../interfaces/IFile';

export interface NamCommentReadContext {
    referralIds: readonly string[];
}

interface NamCommentResponse extends Omit<RequestComment, 'files'> {
    files?: Array<string | IFile>;
}

interface NamCommentThread {
    key: string;
    referralId?: string;
}

const NAM_COMMENT_PAGE_SIZE = 10;
const NAM_COMMENT_READ_CONCURRENCY = 4;

@Injectable({
    providedIn: 'root'
})
export class NamCommentService extends AbstractCommentService {
    protected override attachmentService: AbstractAttachmentService = inject(NamAttachmentService);
    /**
     * @param {string} requestId - id of request the comments belong to
     * @param {string} [cursor] cursor for pagination
     * @param {number} [pageSize=10] maximum number of request per page.
     */
    public getComments(
        requestId: string,
        params: { page?: number; pageSize?: number; cursor?: string },
        readContext?: NamCommentReadContext
    ): Observable<ILegacyPaginatedComments> {
        return readContext
            ? this.getAggregatedComments(requestId, params, readContext)
            : this.getRequestComments(requestId, params);
    }

    private getRequestComments(
        requestId: string,
        params: { page?: number; pageSize?: number; cursor?: string }
    ): Observable<ILegacyPaginatedComments> {
        return this.getThreadPage(
            requestId,
            {
                key: 'request'
            },
            params.cursor,
            params.pageSize ?? NAM_COMMENT_PAGE_SIZE
        ).pipe(
            map(({ response }) => this.mapCursorPaginationToLegacy(response, requestId)),
            catchError((error) => this.handleReadError(error))
        );
    }

    private getAggregatedComments(
        requestId: string,
        params: { page?: number; pageSize?: number; cursor?: string },
        readContext: NamCommentReadContext
    ): Observable<ILegacyPaginatedComments> {
        const pageSize = params.pageSize ?? NAM_COMMENT_PAGE_SIZE;

        return this.drainThreads(requestId, this.getThreads(readContext.referralIds), pageSize).pipe(
            map((data) => ({
                data,
                page: 0,
                pageSize: data.length,
                total: data.length,
                cursor: undefined
            })),
            catchError((error) => this.handleReadError(error))
        );
    }

    private drainThreads(
        requestId: string,
        threads: NamCommentThread[],
        pageSize: number
    ): Observable<IRequestComment[]> {
        const states = new Map(
            threads.map((thread) => [thread.key, { thread, cursor: undefined as string | undefined, exhausted: false }])
        );

        const drainRound = (): Observable<IRequestComment[]> => {
            const pending = [...states.values()].filter((state) => !state.exhausted);
            if (pending.length === 0) {
                return of([]);
            }

            return from(pending).pipe(
                mergeMap(
                    ({ thread, cursor }) => this.getThreadPage(requestId, thread, cursor, pageSize),
                    NAM_COMMENT_READ_CONCURRENCY
                ),
                toArray(),
                concatMap((responses) => {
                    const comments = responses.flatMap(({ thread, response }) =>
                        response.items.map((comment) => this.mapComment(comment, requestId, thread.referralId))
                    );

                    for (const { thread, response } of responses) {
                        const nextCursor = response.metadata.cursor ?? undefined;
                        states.set(thread.key, {
                            thread,
                            cursor: nextCursor,
                            exhausted: !nextCursor
                        });
                    }

                    return drainRound().pipe(map((rest) => comments.concat(rest)));
                })
            );
        };

        return defer(() => drainRound());
    }

    private getThreadPage(
        requestId: string,
        thread: NamCommentThread,
        cursor: string | undefined,
        pageSize: number
    ): Observable<{
        thread: NamCommentThread;
        response: Paginated<NamCommentResponse, PageMetadataWithCursor>;
    }> {
        const queryParams: Record<string, string> = {
            limit: pageSize.toString()
        };

        if (cursor) {
            queryParams['cursor'] = cursor;
        }

        const endpoint = thread.referralId
            ? `${environment.apiUrl}/requests/${requestId}/comments/referrals/${thread.referralId}`
            : `${environment.apiUrl}/requests/${requestId}/comments`;

        return this.httpService
            .get<Paginated<NamCommentResponse, PageMetadataWithCursor>>(endpoint, {
                params: queryParams
            })
            .pipe(map((response) => ({ thread, response })));
    }

    private getThreads(referralIds: readonly string[]): NamCommentThread[] {
        const uniqueReferralIds = [...new Set(referralIds.filter((referralId) => !!referralId))];
        return [
            {
                key: 'request'
            },
            ...uniqueReferralIds.map((referralId) => ({
                key: `referral:${referralId}`,
                referralId
            }))
        ];
    }

    private mapComment(comment: NamCommentResponse, requestId: string, referralId?: string): IRequestComment {
        return {
            ...comment,
            requestId,
            ...(referralId ? { referralId } : {})
        } as IRequestComment;
    }

    private handleReadError(error: unknown): Observable<never> {
        console.error(error);
        return throwError(() => error);
    }

    /**
     * Maps cursor-based pagination response to legacy pagination format.
     * @param {Paginated<RequestComment, PageMetadataWithCursor>} response - The paginated comments from comment-service.
     * @returns {ILegacyPaginatedComments} - The mapped comments in the legacy format.
     */
    private mapCursorPaginationToLegacy(
        response: Paginated<NamCommentResponse, PageMetadataWithCursor>,
        requestId: string
    ): ILegacyPaginatedComments {
        const mappedComments = response.items.map((comment) => this.mapComment(comment, requestId));

        return {
            data: mappedComments,
            page: 0, // Page number is not relevant for cursor-based pagination
            pageSize: response.items.length,
            total: response.items.length, // Total is not available in cursor-based pagination
            cursor: response.metadata.cursor || undefined
        };
    }

    public createComment(
        requestId: string,
        comment: PartialComment,
        internal: boolean,
        notifyResponsible?: boolean
    ): Observable<IRequestComment> {
        const { body, attachments, referralId } = comment;
        const requestBody: {
            body: string;
            internal: boolean;
            notifyResponsible: boolean;
            files?: string[];
            referralId?: string | null;
        } = {
            body: body,
            internal: internal,
            notifyResponsible: notifyResponsible ?? false,
            ...(referralId ? { referralId } : {})
        };
        return this.handleCommentAttachmentUploads(attachments, requestId).pipe(
            concatMap((files) => {
                if (files.length > 0) {
                    requestBody.files = files;
                }
                return this.httpService.post<IRequestComment>(
                    `${environment.apiUrl}/requests/${requestId}/comments`,
                    requestBody
                );
            }),
            catchError((e) => {
                console.error(e);
                this.snackbarService.openSnackbar({
                    type: 'Error',
                    title: 'Comments',
                    desc: 'Failed to create comment.',
                    error: e
                });
                throw e; // Rethrow the error to propagate it to the caller
            })
        );
    }

    public handleCommentAttachmentUploads(
        attachments: UploadFile[] | undefined,
        requestId: string
    ): Observable<string[]> {
        return !!attachments && attachments.length > 0
            ? from(attachments).pipe(
                  mergeMap((attachment) =>
                      this.attachmentService
                          .uploadFile({
                              file: attachment,
                              requestId,
                              fileEntity: EFileEntity.Request
                          })
                          .pipe(map(() => attachment?.name || undefined))
                  ),
                  catchError((e) => {
                      console.error(e);
                      this.snackbarService.openSnackbar({
                          type: 'Error',
                          title: 'Comments',
                          desc: 'Failed to upload attachment.',
                          error: e
                      });
                      throw e; // Rethrow the error to propagate it to the caller
                  }),
                  filter((name): name is string => !!name),
                  toArray()
              )
            : of([]);
    }

    public deleteComment(requestId: string, commentId: string): Observable<void> {
        return this.httpService.delete<void>(`${environment.apiUrl}/requests/${requestId}/comments/${commentId}`).pipe(
            catchError((e) => {
                console.error(e);
                this.snackbarService.openSnackbar({
                    type: 'Error',
                    title: 'Comments',
                    desc: 'Failed to delete comment.',
                    error: e
                });
                return EMPTY;
            })
        );
    }
}
